import React from "react";
import { Star, Trash, Lightning, Calendar, ToggleLeft, ToggleRight } from "@phosphor-icons/react";
import { DOW, describeSchedule, timeUntil } from "@/components/erp/AgentRunHelpers";

/**
 * Saved Queries column inside the agent run modal.
 *
 * Pure-presentational: all state lives in the parent (AgentRunModal). Renders
 * each saved query with its schedule list and inline schedule-create form.
 */
export default function SavedQueriesPanel({
  saved,
  schedules,
  busy,
  openSchedFor,
  setOpenSchedFor,
  schedForm,
  setSchedForm,
  onReplay,
  onRemove,
  onCreateSchedule,
  onToggleSchedule,
  onDeleteSchedule,
}) {
  if (!saved || saved.length === 0) return null;
  const schedsFor = (sid) => schedules.filter((s) => s.saved_query_id === sid);

  return (
    <div data-testid="agent-saved-queries" className="rounded-xl border border-gray-200 bg-amber-50 p-3 shadow-sm">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-600 mb-2 flex items-center gap-2">
        <Star size={12} weight="fill" className="text-amber-500" /> Saved queries · {saved.length}
      </div>
      <div className="space-y-2">
        {saved.map((s) => {
          const ss = schedsFor(s.id);
          return (
            <div key={s.id} className="rounded-lg bg-white border border-gray-200" data-testid={`saved-${s.id}`}>
              <div className="flex items-center justify-between gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{s.label}</div>
                  <div className="text-[11px] text-gray-500 font-mono truncate">
                    {s.run_count > 0 ? `Run ${s.run_count}× · last ${(s.last_run_at || "").slice(0, 10)}` : "Never replayed"}
                  </div>
                </div>
                <button onClick={() => setOpenSchedFor(openSchedFor === s.id ? null : s.id)}
                  data-testid={`agent-add-schedule-${s.id}`}
                  className="text-gray-600 hover:text-[#0020C2] text-xs font-bold inline-flex items-center gap-1 shrink-0"
                  title="Schedule automatic replay">
                  <Calendar size={12} weight="bold" /> {ss.length > 0 ? `+ Add Schedule` : `+ Schedule`}
                </button>
                <button onClick={() => onReplay(s.id)} data-testid={`agent-replay-${s.id}`} disabled={busy}
                  className="text-[#0020C2] hover:underline text-xs font-bold inline-flex items-center gap-1 shrink-0 disabled:opacity-40"
                  title="Replay this query — uses the cached payload, no LLM cost">
                  <Lightning size={12} weight="bold" /> Replay
                </button>
                <button onClick={() => onRemove(s.id)} data-testid={`agent-saved-del-${s.id}`}
                  className="text-[#DC2626] hover:underline text-xs font-bold shrink-0">
                  <Trash size={12} weight="bold" />
                </button>
              </div>

              {ss.length > 0 && (
                <div className="border-t border-gray-100 px-3 py-2 space-y-1.5 bg-gray-50/60 rounded-b-lg">
                  {ss.map((sch) => (
                    <div key={sch.id} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`schedule-${sch.id}`}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="font-mono font-bold text-gray-800 truncate">{describeSchedule(sch)}</span>
                        <span className={`px-1.5 py-0.5 text-[9px] tracking-wider uppercase font-bold rounded ${sch.enabled ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>
                          {sch.enabled ? "Active" : "Paused"}
                        </span>
                        <span className="text-gray-500 font-mono">{sch.enabled ? `next ${timeUntil(sch.next_run_at)}` : "—"}</span>
                        {sch.last_status && (
                          <span className={`px-1.5 py-0.5 text-[9px] tracking-wider uppercase font-bold rounded ${
                            sch.last_status === "ok" ? "bg-green-100 text-green-800"
                            : sch.last_status.includes("error") ? "bg-red-100 text-red-700"
                            : "bg-gray-100 text-gray-600"
                          }`}>last: {sch.last_status}</span>
                        )}
                      </div>
                      <button onClick={() => onToggleSchedule(sch.id)} data-testid={`schedule-toggle-${sch.id}`}
                        className="text-gray-600 hover:text-[#0020C2] shrink-0" title={sch.enabled ? "Pause" : "Resume"}>
                        {sch.enabled ? <ToggleRight size={16} weight="fill" /> : <ToggleLeft size={16} weight="bold" />}
                      </button>
                      <button onClick={() => onDeleteSchedule(sch.id)} data-testid={`schedule-del-${sch.id}`}
                        className="text-[#DC2626] hover:underline font-bold shrink-0">
                        <Trash size={12} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {openSchedFor === s.id && (
                <div className="border-t border-gray-100 px-3 py-3 bg-blue-50 space-y-2 rounded-b-lg" data-testid={`sched-form-${s.id}`}>
                  <div className="grid grid-cols-4 gap-2">
                    <select value={schedForm.cadence}
                      onChange={(e) => setSchedForm({ ...schedForm, cadence: e.target.value })}
                      data-testid="sched-cadence"
                      className="border border-gray-300 px-2 py-1.5 text-xs rounded-md">
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    {schedForm.cadence === "weekly" && (
                      <select value={schedForm.day_of_week}
                        onChange={(e) => setSchedForm({ ...schedForm, day_of_week: parseInt(e.target.value) })}
                        data-testid="sched-dow"
                        className="border border-gray-300 px-2 py-1.5 text-xs rounded-md">
                        {DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}
                      </select>
                    )}
                    {schedForm.cadence === "monthly" && (
                      <select value={schedForm.day_of_month}
                        onChange={(e) => setSchedForm({ ...schedForm, day_of_month: parseInt(e.target.value) })}
                        data-testid="sched-dom"
                        className="border border-gray-300 px-2 py-1.5 text-xs rounded-md">
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>Day {d}</option>)}
                      </select>
                    )}
                    {schedForm.cadence === "daily" && <span />}
                    <select value={schedForm.hour_utc}
                      onChange={(e) => setSchedForm({ ...schedForm, hour_utc: parseInt(e.target.value) })}
                      data-testid="sched-hour"
                      className="border border-gray-300 px-2 py-1.5 text-xs font-mono rounded-md">
                      {Array.from({ length: 24 }, (_, i) => i).map((h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00 UTC`}</option>)}
                    </select>
                    <button onClick={() => onCreateSchedule(s.id)} data-testid={`sched-save-${s.id}`}
                      className="rounded-md bg-[#0020C2] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3]">
                      Save
                    </button>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-gray-600">
                    <span>UTC clock — convert to your local TZ before setting.</span>
                    <button onClick={() => setOpenSchedFor(null)} className="hover:text-gray-900">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
